import { useEffect, useMemo, useRef, useState } from "react";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import "quill/dist/quill.snow.css";
import InlineError from "@components/forms/InlineError";
import MediaPicker from "@components/media/MediaPicker";
import AppModal from "@components/modals/AppModal";
import useLanguages from "@hooks/languages/useLanguages";
import HomeLocalizedField from "@components/home_content/fields/HomeLocalizedField";
import {
  getHomeContentRequest,
  saveHomeContentRequest,
} from "@services/homeContent/homeContentApi";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";

const LOCALIZED_HOME_FIELDS = [
  { name: "title", apiName: "title", label: "title" },
  {
    name: "description",
    apiName: "description",
    label: "description",
    multiline: true,
  },
  {
    name: "tourButtonTitle",
    apiName: "tour_button_title",
    label: "tour button title",
  },
  {
    name: "carbonFootPrintTitle",
    apiName: "carbon_foot_print_title",
    label: "carbon foot print title",
  },
  { name: "faqsTitle", apiName: "faqs_title", label: "faqs title" },
  { name: "seeAllLabel", apiName: "see_all_label", label: "see all label" },
];

function buildLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

function buildLocalizedInitialValues(languageList = []) {
  const values = LOCALIZED_HOME_FIELDS.reduce((acc, field) => {
    acc[field.name] = languageList.reduce((localized, language) => {
      localized[language.iso] = {
        id: "",
        text: "",
      };
      return localized;
    }, {});
    return acc;
  }, {});
  values.id = "";
  values.logo = null;
  values.logoUrl = {
    url: "",
    id: "",
  };
  return values;
}

function normalizeLocalizedText(value, languageList = []) {
  return languageList.reduce((acc, language) => {
    const localizedValue = value?.[language.iso];
    if (
      localizedValue &&
      typeof localizedValue === "object" &&
      !Array.isArray(localizedValue)
    ) {
      acc[language.iso] = {
        id: localizedValue.id || "",
        text: localizedValue.text || "",
      };
      return acc;
    }
    acc[language.iso] = {
      id: "",
      text: typeof localizedValue === "string" ? localizedValue : "",
    };
    return acc;
  }, {});
}

function buildMediaFromLogo(logo = {}) {
  const resolvedUrl = resolveMediaUrl(logo?.url || "");
  if (!resolvedUrl && !logo?.id) return null;
  return {
    id: logo?.id || "",
    sourceId: logo?.id || "",
    url: resolvedUrl,
    mediaType: inferMediaType(resolvedUrl),
    mimeType: "",
  };
}

function normalizeHomeContent(home, languageList = []) {
  const baseValues = buildLocalizedInitialValues(languageList);
  if (!home || typeof home !== "object") return baseValues;
  const nextValues = {
    ...baseValues,
    id: home?.id || "",
    logoUrl: {
      url: home?.logo?.url || "",
      id: home?.logo?.id || "",
    },
    logo: buildMediaFromLogo(home?.logo || {}),
  };
  LOCALIZED_HOME_FIELDS.forEach((field) => {
    nextValues[field.name] = normalizeLocalizedText(
      home?.[field.apiName],
      languageList,
    );
  });
  return nextValues;
}

function buildLocalizedValidationSchema(languageList = []) {
  const localizedShape = languageList.reduce((acc, language) => {
    acc[language.iso] = yup.object({
      id: yup.string().default(""),
      text: yup
        .string()
        .trim()
        .required(`${language.iso.toUpperCase()} is required.`),
    });
    return acc;
  }, {});

  return yup.object({
    ...LOCALIZED_HOME_FIELDS.reduce((acc, field) => {
      acc[field.name] = yup.object().shape(localizedShape);
      return acc;
    }, {}),
    logoUrl: yup.object({
      url: yup.string().trim().required("Logo is required."),
      id: yup.string().default(""),
    }),
  });
}

function buildTouchedValues(languageList = []) {
  const touched = LOCALIZED_HOME_FIELDS.reduce((acc, field) => {
    acc[field.name] = languageList.reduce((localized, language) => {
      localized[language.iso] = {
        text: true,
      };
      return localized;
    }, {});
    return acc;
  }, {});
  touched.logoUrl = {
    url: true,
  };
  return touched;
}

function buildHomePayload(values, languageList = []) {
  const payload = {
    id: values?.id || "",
    logo: {
      url: values?.logoUrl?.url || "",
      id: values?.logoUrl?.id || "",
    },
  };

  LOCALIZED_HOME_FIELDS.forEach((field) => {
    payload[field.apiName] = languageList.reduce((acc, language) => {
      acc[language.iso] = {
        text: values?.[field.name]?.[language.iso]?.text || "",
        id: values?.[field.name]?.[language.iso]?.id || "",
      };
      return acc;
    }, {});
  });

  return payload;
}

function HomeSection() {
  const { languages, loading, error } = useLanguages();
  const languageList = useMemo(() => buildLanguageList(languages), [languages]);
  const [homeContent, setHomeContent] = useState(null);
  const [fetchingHome, setFetchingHome] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const initialValues = useMemo(
    () => normalizeHomeContent(homeContent, languageList),
    [homeContent, languageList],
  );
  const validationSchema = useMemo(
    () => buildLocalizedValidationSchema(languageList),
    [languageList],
  );
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState("");
  const [editor, setEditor] = useState({
    isOpen: false,
    languageIso: "",
  });
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const currentLanguageIso = languageList.some(
    (language) => language.iso === activeLanguageIso,
  )
    ? activeLanguageIso
    : languageList[0]?.iso || "";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      setLocalError("");
      try {
        const response = await saveHomeContentRequest(
          buildHomePayload(values, languageList),
        );
        setHomeContent(response?.body || null);
      } catch (submitError) {
        setLocalError(submitError?.message || "Could not save home content.");
        throw submitError;
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!languageList.length) return;
    formik.setTouched(buildTouchedValues(languageList), true);
  }, [languageList]);

  useEffect(() => {
    let isMounted = true;
    async function fetchHomeContent() {
      setFetchingHome(true);
      setLocalError("");
      try {
        const response = await getHomeContentRequest();
        if (!isMounted) return;
        setHomeContent(response?.body || null);
      } catch (fetchError) {
        if (!isMounted) return;
        setLocalError(fetchError?.message || "Could not load home content.");
      } finally {
        if (isMounted) setFetchingHome(false);
      }
    }
    fetchHomeContent();
    return () => {
      isMounted = false;
    };
  }, []);

  function handleLocalizedChange(fieldName, languageIso, nextValue) {
    formik.setFieldValue(`${fieldName}.${languageIso}.text`, nextValue, true);
  }

  function handleLocalizedBlur(fieldName, languageIso) {
    formik.setFieldTouched(`${fieldName}.${languageIso}.text`, true, true);
  }

  function getLocalizedFieldError(fieldName, languageIso) {
    const touched = getIn(formik.touched, `${fieldName}.${languageIso}.text`);
    const error = getIn(formik.errors, `${fieldName}.${languageIso}.text`);
    return touched ? error || "" : "";
  }

  function getFieldError(path) {
    const touched = getIn(formik.touched, path);
    const error = getIn(formik.errors, path);
    return touched ? error || "" : "";
  }

  function openDescriptionEditor(languageIso, value = "") {
    setEditor({
      isOpen: true,
      languageIso,
    });
    setDescriptionHtml(value || "");
  }

  function closeDescriptionEditor() {
    setEditor({
      isOpen: false,
      languageIso: "",
    });
    setIsEditorReady(false);
  }

  function saveDescriptionEditor() {
    if (!editor.languageIso) return;
    formik.setFieldValue(
      `description.${editor.languageIso}.text`,
      descriptionHtml,
      true,
    );
    formik.setFieldTouched(
      `description.${editor.languageIso}.text`,
      true,
      true,
    );
    closeDescriptionEditor();
  }

  async function handleUploadLogo(files = []) {
    const [file] = Array.isArray(files) ? files : [];
    if (!file) return;
    setUploadingLogo(true);
    setLogoUploadError("");
    try {
      const response = await createMultimediaRequest({ file });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not upload logo.");
      const media = {
        id: rawItem?.id || rawItem?.mmd_id || "",
        sourceId: rawItem?.id || rawItem?.mmd_id || "",
        url: resolveMediaUrl(rawItem?.url || rawItem?.mmd_url || ""),
        mediaType:
          rawItem?.mediaType ||
          rawItem?.mmd_media_type ||
          inferMediaType(rawItem?.url || rawItem?.mmd_url || ""),
        mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
      };
      formik.setFieldValue("logo", media, false);
      formik.setFieldValue(
        "logoUrl",
        {
          url: media.url || "",
          id: media.id || media.sourceId || "",
        },
        true,
      );
    } catch (uploadError) {
      setLogoUploadError(uploadError?.message || "Could not upload logo.");
      formik.setFieldTouched("logoUrl.url", true, true);
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleSave() {
    formik.setTouched(buildTouchedValues(languageList), true);
    try {
      await formik.submitForm();
    } catch (error) {}
  }

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillInstanceRef.current) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: descriptionHtml,
    });
    if (!quill) return;
    quillHandlerRef.current = () => setDescriptionHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsEditorReady(true);
  }, [descriptionHtml, editor.isOpen, quillHost]);

  useEffect(() => {
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  useEffect(
    () => () => {
      if (!quillInstanceRef.current) return;
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
    },
    [quillHost],
  );

  return (
    <section className="home-content-section-card">
      <div className="home-content-form-head">
        <div>
          <h3>Home</h3>
          <p>
            Manage the localized labels and copy for the Home content section.
          </p>
        </div>
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${currentLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={currentLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {loading || fetchingHome ? (
        <p className="form-helper">Loading data...</p>
      ) : null}
      {error ? <p className="message-error">{error}</p> : null}
      {localError ? <p className="message-error">{localError}</p> : null}

      {!loading && !fetchingHome && languageList.length ? (
        <form
          className="home-content-form home-content-form--home tour-add-form"
          onSubmit={formik.handleSubmit}
        >
          <div className="home-content-form-shell">
            <div className="home-content-form-grid home-content-form-grid--home">
              <div className="tour-add-form-group">
                <p className="tour-add-form-label">
                  logo
                  {getFieldError("logoUrl.url") ? (
                    <InlineError>{getFieldError("logoUrl.url")}</InlineError>
                  ) : null}
                </p>
                <MediaPicker
                  title="logo (required)"
                  titleError=""
                  items={formik.values?.logo ? [formik.values.logo] : []}
                  isUploading={uploadingLogo}
                  uploadError={logoUploadError}
                  accept={{ "image/*": [] }}
                  maxFiles={1}
                  invalidTypeMessage="Image only for this section."
                  onRejected={(message) => setLogoUploadError(message)}
                  onUpload={handleUploadLogo}
                  onRemove={() => {
                    formik.setFieldValue("logo", null, false);
                    formik.setFieldValue("logoUrl", { url: "", id: "" }, true);
                    formik.setFieldTouched("logoUrl.url", true, true);
                  }}
                  uploadLabel="Upload home logo"
                  uploadHint="Only 1 image is allowed for this section."
                />
              </div>
              {LOCALIZED_HOME_FIELDS.map((field) =>
                field.name === "description" ? (
                  <div className="tour-add-form-group" key={field.name}>
                    <p className="tour-add-form-label">
                      {field.label}
                      {getLocalizedFieldError(field.name, currentLanguageIso) ? (
                        <InlineError>
                          {getLocalizedFieldError(field.name, currentLanguageIso)}
                        </InlineError>
                      ) : null}
                    </p>
                    <button
                      type="button"
                      className={`field-label app-form-field field-preview-row ${
                        formik.values?.description?.[currentLanguageIso]?.text
                          ? "filled"
                          : ""
                      }`}
                      onClick={() =>
                        openDescriptionEditor(
                          currentLanguageIso,
                          formik.values?.description?.[currentLanguageIso]
                            ?.text || "",
                        )
                      }
                    >
                      {formik.values?.description?.[currentLanguageIso]?.text ? (
                        <span
                          className="field-preview-value field-preview-value-html"
                          dangerouslySetInnerHTML={{
                            __html: String(
                              formik.values?.description?.[currentLanguageIso]
                                ?.text || "",
                            ),
                          }}
                        />
                      ) : (
                        <span className="field-preview-value">Complete</span>
                      )}
                    </button>
                  </div>
                ) : (
                  <HomeLocalizedField
                    key={field.name}
                    label={field.label}
                    fieldName={field.name}
                    activeLanguageIso={currentLanguageIso}
                    value={
                      formik.values?.[field.name]?.[currentLanguageIso]?.text ||
                      ""
                    }
                    onChange={handleLocalizedChange}
                    onBlur={handleLocalizedBlur}
                    error={getLocalizedFieldError(field.name, currentLanguageIso)}
                    multiline={Boolean(field.multiline)}
                    placeholder={`(${currentLanguageIso})`}
                  />
                ),
              )}
            </div>
          </div>
          <div className="mode-route-tool-steps tour-add-save-row">
            <button
              type="button"
              className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${!formik.isValid || submitting ? "is-disabled" : ""} ${submitting ? "is-loading" : ""}`}
              onClick={handleSave}
              disabled={!formik.isValid || submitting}
              aria-disabled={!formik.isValid || submitting}
              aria-label="Save home content"
              title="Save home content"
            >
              {submitting ? (
                <span className="mode-route-tool-btn-spinner" />
              ) : (
                <>
                  Save
                  <IconDeviceFloppy size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      ) : null}

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeDescriptionEditor}
        title={`description (${editor.languageIso})`}
        size="lg"
      >
        <div className="tour-add-editor">
          {!isEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveDescriptionEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeDescriptionEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>
    </section>
  );
}

export default HomeSection;
